import { format, parseISO } from 'date-fns';
import { useRouter } from 'next/compat/router';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

import BackButton from '@/components/Buttons/BackButton';
import ScrollToTop from '@/components/Buttons/ScrollToTop';
import Container from '@/components/Container/Container';
import Footer from '@/components/Footer/Footer';
import Navigation from '@/components/Navigation/Navigation';
import RelevantPosts from '@/components/RelevantPosts/RelevantPosts';
import Seo from '@/components/Seo/Seo';
import { BLOG_POSTS_API_LIMIT, LANGUAGES } from '@/lib/constants';
import { fetchAPI, getImage } from '@/lib/fetchAPI';
import { parseHTML } from '@/lib/parse';
import { translate } from '@/lib/translate';
import Recommended from '@/modules/blog/Recommended';
import Tags from '@/modules/blog/Tags';

const ShareBlock = ({ id, lang, postUrl, shareButtons }: any) => {
	const [copiedPopupActive, setCopiedPopupActive] = useState(false);

	return (
		<Share>
			<ShareTitle>
				{translate(lang, {
					en: 'Share article',
					ru: 'Поделиться статьей',
					ua: 'Поділитися статтею',
				})}
			</ShareTitle>

			{shareButtons.map((item, index) => (
				<ShareButton
					key={`share_button_${id}_${index}`}
					href={item.link}
					target="_blank"
					rel="noopener noreferrer"
					aria-label={`Share with ${item.name}`}
					onClick={(e) => {
						if (item.name === 'copy') {
							e.preventDefault();

							navigator.clipboard.writeText(postUrl);

							setCopiedPopupActive(true);

							setTimeout(() => {
								setCopiedPopupActive(false);
							}, 3000);
						}
					}}
				>
					{parseHTML(item.icon)}
				</ShareButton>
			))}

			<CopiedPopup $active={copiedPopupActive}>
				{translate(lang, {
					en: 'The link is copied',
					ru: 'Ссылка скопирована',
					ua: 'Посилання скопійоване',
				})}
			</CopiedPopup>
		</Share>
	);
};

const PostPage: FC<any> = ({ url, lang, post, recommended, related }) => {
	const router = useRouter();
	const [content, setContent] = useState<any>(null);
	const postUrl = 'https://www.forbusiness.viber.com' + router?.asPath;
	const shareButtons = [
		{
			name: 'viber',
			link: `viber://forward?text=${postUrl}`,
			icon: '<svg width="21" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.6984 2.63475C18.1695 2.16143 16.0538 0.602252 11.2936 0.574409C11.2936 0.574409 5.69817 0.240297 2.97007 2.74612C1.43899 4.27745 0.910075 6.47701 0.8544 9.23341C0.798724 11.9898 0.715218 17.1407 5.69817 18.5606V20.7045C5.69817 20.7045 5.67034 21.5676 6.22709 21.7347C6.92303 21.9574 7.31276 21.2892 7.98087 20.5931C8.34276 20.2033 8.84383 19.6465 9.20572 19.201C12.6019 19.4794 15.2187 18.839 15.5249 18.7277C16.2208 18.5049 20.0903 18.0038 20.7306 12.8529C21.3987 7.50718 20.4522 4.13824 18.6984 2.63475ZM19.283 12.4353C18.7541 16.7509 15.6084 17.0293 15.0238 17.1963C14.7733 17.2799 12.4906 17.8367 9.59546 17.6697C9.59546 17.6697 7.45195 20.259 6.75601 20.9551C6.64465 21.0664 6.5333 21.0943 6.44979 21.0943C6.33844 21.0664 6.3106 20.9272 6.3106 20.7323C6.3106 20.4539 6.33844 17.1685 6.33844 17.1685C2.13494 15.9991 2.38548 11.6 2.41332 9.28909C2.46899 6.97817 2.88656 5.11272 4.1671 3.83197C6.47763 1.74379 11.2379 2.05006 11.2379 2.05006C15.2465 2.0779 17.1673 3.27512 17.6127 3.69276C19.0603 4.97351 19.8119 8.00835 19.283 12.4353Z" fill="#7360F2"/><path fill-rule="evenodd" clip-rule="evenodd" d="M11.3559 7.43683C11.364 7.28697 11.492 7.17205 11.6419 7.18015C12.2024 7.21046 12.6661 7.37997 12.9957 7.72249C13.3238 8.0634 13.4833 8.53865 13.5132 9.10783C13.5211 9.25771 13.406 9.3856 13.2562 9.39348C13.1063 9.40137 12.9784 9.28627 12.9705 9.13639C12.9448 8.64752 12.8119 8.31531 12.6041 8.09935C12.3978 7.885 12.082 7.74823 11.6126 7.72285C11.4627 7.71475 11.3478 7.58669 11.3559 7.43683Z" fill="#7360F2"/><path fill-rule="evenodd" clip-rule="evenodd" d="M10.995 5.9823C11.0068 5.83269 11.1377 5.72098 11.2873 5.73279C12.3983 5.82052 13.3033 6.20454 13.9717 6.93383L13.9721 6.93422C14.6078 7.63058 14.9357 8.53592 14.906 9.57542C14.9017 9.72544 14.7767 9.84358 14.6266 9.8393C14.4766 9.83501 14.3585 9.70992 14.3628 9.5599C14.3887 8.65046 14.1043 7.88527 13.5709 7.30085C13.0144 6.69377 12.2492 6.35392 11.2445 6.2746C11.0949 6.26278 10.9832 6.13192 10.995 5.9823Z" fill="#7360F2"/><path fill-rule="evenodd" clip-rule="evenodd" d="M10.4648 4.63939C10.4648 4.48931 10.5865 4.36765 10.7366 4.36765C12.3067 4.36765 13.6286 4.89375 14.6843 5.92027C15.7477 6.95437 16.2977 8.36573 16.2977 10.1246C16.2977 10.2746 16.176 10.3963 16.026 10.3963C15.8759 10.3963 15.7542 10.2746 15.7542 10.1246C15.7542 8.48648 15.2463 7.22487 14.3054 6.30992C13.3567 5.3874 12.1731 4.91114 10.7366 4.91114C10.5865 4.91114 10.4648 4.78947 10.4648 4.63939Z" fill="#7360F2"/><path d="M11.6834 12.7137C11.6834 12.7137 12.0732 12.7416 12.268 12.491L12.6578 11.9898C12.8526 11.7392 13.298 11.5722 13.7713 11.8228C14.0218 11.962 14.4672 12.2404 14.7456 12.4631C15.0518 12.6859 15.6642 13.187 15.6642 13.187C15.9705 13.4376 16.0261 13.7996 15.8313 14.1894C15.6364 14.5513 15.358 14.8854 14.9961 15.1917C14.7178 15.4423 14.4394 15.5536 14.161 15.6093H14.0497C13.9383 15.6093 13.7991 15.5815 13.6878 15.5536C13.2702 15.4423 12.5743 15.136 11.3772 14.4956C10.6256 14.078 9.98534 13.6325 9.45643 13.2149C9.17805 12.9921 8.87183 12.7416 8.59345 12.4353L8.4821 12.3239C8.17589 12.0177 7.92534 11.7392 7.70264 11.4608C7.28508 10.9318 6.83967 10.2914 6.4221 9.53967C5.78184 8.37029 5.47562 7.67423 5.36427 7.22875C5.33643 7.11738 5.30859 7.00602 5.30859 6.8668V6.75543C5.33643 6.477 5.47562 6.19858 5.72616 5.92015C6.03238 5.58604 6.36643 5.30762 6.72832 5.08488C7.11805 4.88998 7.47994 4.94567 7.73048 5.25194C7.73048 5.25194 8.23156 5.86448 8.45427 6.17074C8.64913 6.44917 8.9275 6.89465 9.09453 7.14523C9.34507 7.59071 9.17804 8.06403 8.95534 8.25893L8.45427 8.64872C8.20373 8.84362 8.23156 9.23341 8.23156 9.23341C8.23156 9.23341 8.92751 11.9898 11.6834 12.7137Z" fill="#7360F2"></svg>',
		},
		{
			name: 'linkedin',
			link: `https://www.linkedin.com/sharing/share-offsite/?url=${postUrl}`,
			icon: '<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.81906 0C0.719147 0 0 0.722259 0 1.67157C0 2.59991 0.697723 3.34277 1.77686 3.34277H1.79773C2.91916 3.34277 3.61706 2.59991 3.61706 1.67157C3.5961 0.722259 2.91916 0 1.81906 0Z" fill="#7360F2"/><path d="M0.190046 4.66354H3.40556V14.3375H0.190046V4.66354Z" fill="#7360F2"/><path d="M11.2982 4.43654C9.56355 4.43654 8.40038 6.06655 8.40038 6.06655V4.66359H5.18477V14.3376H8.40019V8.93526C8.40019 8.64605 8.42116 8.35729 8.50612 8.15047C8.73857 7.57296 9.26756 6.97466 10.1559 6.97466C11.3194 6.97466 11.7848 7.86181 11.7848 9.16231V14.3376H15V8.7907C15 5.81926 13.4136 4.43654 11.2982 4.43654Z" fill="#7360F2"></svg>',
		},
		{
			name: 'facebook',
			link: `https://www.facebook.com/sharer.php?u=${postUrl}`,
			icon: '<svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.99841 0.00332907L6.92359 0C4.5926 0 3.08621 1.54552 3.08621 3.93762V5.75313H1.00007C0.819806 5.75313 0.673828 5.89927 0.673828 6.07954V8.71001C0.673828 8.89028 0.819973 9.03625 1.00007 9.03625H3.08621V15.6738C3.08621 15.854 3.23219 16 3.41246 16H6.13428C6.31454 16 6.46052 15.8539 6.46052 15.6738V9.03625H8.89971C9.07997 9.03625 9.22595 8.89028 9.22595 8.71001L9.22695 6.07954C9.22695 5.99299 9.19249 5.91009 9.13141 5.84884C9.07032 5.78758 8.98709 5.75313 8.90054 5.75313H6.46052V4.2141C6.46052 3.47438 6.63679 3.09886 7.60038 3.09886L8.99808 3.09836C9.17818 3.09836 9.32416 2.95222 9.32416 2.77211V0.329578C9.32416 0.149642 9.17835 0.00366197 8.99841 0.00332907Z" fill="#7360F2"></svg>',
		},
		{
			name: 'twitter',
			link: `http://twitter.com/share?text=${post?.article?.title}&url=${postUrl}`,
			icon: '<svg width="16" height="13" viewBox="0 0 16 13" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 1.539C15.405 1.8 14.771 1.973 14.11 2.057C14.79 1.651 15.309 1.013 15.553 0.244C14.919 0.622 14.219 0.889 13.473 1.038C12.871 0.397 12.013 0 11.077 0C9.261 0 7.799 1.474 7.799 3.281C7.799 3.541 7.821 3.791 7.875 4.029C5.148 3.896 2.735 2.589 1.114 0.598C0.831 1.089 0.665 1.651 0.665 2.256C0.665 3.392 1.25 4.399 2.122 4.982C1.595 4.972 1.078 4.819 0.64 4.578C0.64 4.588 0.64 4.601 0.64 4.614C0.64 6.208 1.777 7.532 3.268 7.837C3.001 7.91 2.71 7.945 2.408 7.945C2.198 7.945 1.986 7.933 1.787 7.889C2.212 9.188 3.418 10.143 4.852 10.174C3.736 11.047 2.319 11.573 0.785 11.573C0.516 11.573 0.258 11.561 0 11.528C1.453 12.465 3.175 13 5.032 13C11.068 13 14.368 8 14.368 3.666C14.368 3.521 14.363 3.381 14.356 3.242C15.007 2.78 15.554 2.203 16 1.539Z" fill="#7360F2"></svg>',
		},
		{
			name: 'mail',
			link: `mailto:?subject=${post?.article?.title}&body=${postUrl}`,
			icon: '<svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M2.36128 0.106201H13.6387C14.7532 0.106201 15.6978 0.899589 15.9244 1.95744L8.00944 6.79333L0.0755615 1.95744C0.302244 0.899589 1.24675 0.106201 2.36128 0.106201ZM7.99056 8.15334C8.14168 8.15334 8.31169 8.11556 8.46281 8.02111L16 3.43079V9.53233C16 10.8358 14.9421 11.8936 13.6387 11.8936H2.36128C1.05785 11.8936 0 10.8358 0 9.53233V3.43079L7.5183 8.02111C7.66942 8.11556 7.83943 8.15334 7.99056 8.15334Z" fill="#7360F2"></svg>',
		},
		{
			name: 'copy',
			icon: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_9523_36941)"><path d="M9.76314 6.23653C9.57361 6.04587 9.36346 5.87687 9.13659 5.73262C8.5357 5.34784 7.83714 5.14338 7.12361 5.14338C6.13326 5.14153 5.18315 5.53503 4.48409 6.23653L1.0927 9.63057C0.394821 10.3298 0.00257879 11.2772 0.0019539 12.265C0.000641634 14.3265 1.67072 15.9988 3.73222 16.0001C4.72104 16.0035 5.67028 15.6119 6.36909 14.9123L9.16859 12.1128C9.21945 12.0624 9.24786 11.9936 9.24745 11.9219C9.2466 11.7747 9.12653 11.656 8.97928 11.6569H8.87264C8.28775 11.6589 7.70807 11.5466 7.16629 11.3263C7.06647 11.2852 6.95174 11.3084 6.87569 11.3849L4.86271 13.4006C4.23763 14.0256 3.22419 14.0256 2.59911 13.4006C1.97404 12.7755 1.97404 11.762 2.59911 11.137L6.00384 7.73491C6.62842 7.11112 7.64021 7.11112 8.26478 7.73491C8.68571 8.13106 9.34228 8.13106 9.76317 7.73491C9.94426 7.55366 10.0541 7.31327 10.0725 7.05769C10.0919 6.75228 9.97929 6.45317 9.76314 6.23653Z" fill="#7360F2"/><path d="M14.9045 1.09317C13.4468 -0.364595 11.0833 -0.364595 9.62549 1.09317L6.82865 3.88733C6.75185 3.9645 6.7297 4.08058 6.77266 4.18062C6.81512 4.28095 6.91435 4.34534 7.02327 4.34325H7.12191C7.70611 4.34219 8.28485 4.45538 8.8256 4.67653C8.92543 4.71762 9.04015 4.69443 9.1162 4.61789L11.1238 2.6129C11.7489 1.98783 12.7624 1.98783 13.3874 2.6129C14.0125 3.23798 14.0125 4.25142 13.3874 4.87649L10.8865 7.3747L10.8652 7.3987L9.9907 8.26789C9.36613 8.89168 8.35434 8.89168 7.72977 8.26789C7.30884 7.87174 6.65227 7.87174 6.23138 8.26789C6.04913 8.45051 5.93924 8.693 5.92209 8.95042C5.90259 9.25583 6.01523 9.55494 6.23138 9.77162C6.53998 10.0815 6.90104 10.3343 7.29784 10.5181C7.35383 10.5448 7.40982 10.5661 7.46581 10.5901C7.5218 10.6141 7.58045 10.6328 7.63644 10.6541C7.69243 10.6755 7.75107 10.6941 7.80706 10.7101L7.96438 10.7528C8.07102 10.7794 8.17768 10.8007 8.28698 10.8194C8.41864 10.839 8.55124 10.8514 8.68425 10.8567H8.87087H8.88687L9.04684 10.8381C9.10549 10.8354 9.16682 10.8221 9.23615 10.8221H9.32679L9.51076 10.7954L9.59609 10.7794L9.75072 10.7474H9.78005C10.435 10.5829 11.033 10.2438 11.5104 9.76627L14.9044 6.37223C16.3623 4.91446 16.3623 2.55094 14.9045 1.09317Z" fill="#7360F2"></g><defs><clipPath id="clip0_9523_36941"><rect width="16" height="16" fill="white"></clipPath></defs></svg>',
		},
	];

	const handleBackButton = () => router?.push(`/${lang}/blog`);

	useEffect(() => {
		if (!post?.article?.description) return;

		const inner = post.article.description;
		const container = document.createElement('div');

		container.innerHTML = inner;

		const figures = container.querySelectorAll('figure.media');

		figures?.forEach((item) => {
			const youTubeVideoId = item
				?.querySelector('oembed')
				?.getAttribute('url')
				?.split('=')
				?.pop();
			const embedIframe = document.createElement('iframe');
			const embedUrl = 'https://www.youtube.com/embed/' + youTubeVideoId;

			embedIframe.setAttribute('src', embedUrl);
			embedIframe.setAttribute('width', '100%');
			embedIframe.setAttribute('height', '500');
			embedIframe.setAttribute('frameborder', '0');
			embedIframe.setAttribute('allowfullscreen', '');

			item.before(embedIframe);
			item.remove();
		});

		const links = container?.querySelectorAll('a');

		links?.forEach((link) => {
			const href = link.getAttribute('href');
			const isInnerLink = href?.includes('forbusiness.viber.com');
			const rel = isInnerLink ? 'prefetch' : 'noopener noreferrer';
			const target = isInnerLink ? '_self' : '_blank';

			link.setAttribute('rel', rel);
			link.setAttribute('target', target);
		});

		const _content = parseHTML(container.innerHTML);

		setContent(_content);
	}, [post?.article?.description]);

	return (
		<>
			<Seo
				url={`/blog/${url}`}
				postImage={post?.preview}
				imageUrl={post?.preview?.url}
				dateModified={post?.updatedAt}
				datePublished={post?.publishedAt}
				authorName={post?.author?.author?.name}
				title={'Blog | ' + (post?.article?.seoTitle || post?.article?.title)}
				description={
					post?.article?.seoDescription || post?.article?.shortDescription
				}
			/>

			<Navigation lang={lang} />

			<Wrapper>
				<Container>
					<Row>
						<Left>
							<LeftBlock>
								<BackButton
									text={translate(lang, {
										en: 'All articles',
										ru: 'Все статьи',
										ua: 'Всі статті',
									})}
									onClick={handleBackButton}
								/>

								<ShareBlock
									id="desktop"
									lang={lang}
									postUrl={postUrl}
									shareButtons={shareButtons}
								/>
							</LeftBlock>
						</Left>

						<Center>
							<PostContainer>
								<PostTitle>{parseHTML(post?.article?.title)}</PostTitle>

								<MetaRow>
									{post?.author?.author ? (
										<Author>
											<AuthorImage
												alt={post.author.author.name}
												src={getImage(post.author.authorImage)}
											/>

											<div>
												<Name>{parseHTML(post.author.author.name)}</Name>

												<Position>{post.author.author.position}</Position>
											</div>
										</Author>
									) : null}

									{post?.date ? (
										<PostDate>
											{format(parseISO(post?.date), 'MMMM d, y')}
										</PostDate>
									) : null}
								</MetaRow>

								<ShareBlock
									id="mobile_1"
									lang={lang}
									postUrl={postUrl}
									shareButtons={shareButtons}
								/>

								<Preview
									alt={post?.preview?.name}
									src={getImage(post?.preview)}
								/>

								{content ? <Content id="content">{content}</Content> : null}

								<ShareBlock
									id="mobile_2"
									lang={lang}
									postUrl={postUrl}
									shareButtons={shareButtons}
								/>
							</PostContainer>
						</Center>

						<Right>
							{recommended?.length > 0 && <Recommended data={recommended} />}

							<Tags data={post?.tags} />
						</Right>
					</Row>

					<ScrollToTop />
				</Container>

				<RelevantPosts
					data={related}
					title={translate(lang, {
						en: 'Related posts',
						ru: 'Похожие статьи',
						ua: 'Схожi статті',
					})}
				/>
			</Wrapper>

			<Footer lang={lang} />
		</>
	);
};

const Wrapper = styled.div`
	margin-top: 81px;

	@media (max-width: 500px) {
		padding: 0 5px;
	}
`;

const Row = styled.div`
	display: flex;
	justify-content: space-between;

	@media (max-width: 690px) {
		flex-direction: column;
	}
`;

const Share = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	background-color: var(--color-white-30);
	padding: 15px;
	width: 140px;
	row-gap: 10px;
	border-radius: 12px;
	margin: 38px auto 0;
	position: relative;

	@media (max-width: 900px) {
		width: 100%;
		margin-bottom: 34px;
	}
`;

const ShareTitle = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	font-family: var(--font-sans);
	font-weight: var(--font-medium);
	font-size: 14px;
	line-height: 20px;
	margin-bottom: 5px;
	color: var(--color-purple-50);

	@media (max-width: 900px) {
		width: max-content;
		column-gap: 10px;
	}

	svg {
		width: 15px;
		height: 15px;

		path {
			fill: var(--color-purple-50);
		}
	}

	@media (max-width: 900px) {
		margin-bottom: 0;
	}
`;

const ShareButton = styled.a`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 50px;
	height: 50px;
	background-color: var(--color-white);
	border-radius: 12px;
	cursor: pointer;
	transition-duration: 0.5s;

	@media (max-width: 900px) {
		width: 20px;
		height: 20px;
		background-color: transparent;
	}

	@media (min-width: 901px) {
		svg {
			path {
				transition-duration: 0.5s;
			}
		}

		&:hover {
			background-color: var(--color-purple);

			svg {
				path {
					fill: var(--color-white);
				}
			}
		}
	}
`;

const CopiedPopup = styled.div<{ $active: boolean }>`
	position: absolute;
	bottom: -50px;
	left: 0;
	width: 100%;
	display: flex;
	justify-content: center;
	padding: 10px;
	border-radius: 12px;
	background-color: var(--color-purple);
	color: var(--color-white);
	font-family: var(--font-sans);
	font-size: 14px;
	font-weight: var(--font-medium);
	line-height: 20px;
	pointer-events: none;
	opacity: 0;
	transition-duration: 0.5s;

	@media (max-width: 900px) {
		bottom: 0;
		height: 100%;
		align-items: center;
	}

	${({ $active }) => $active && 'opacity: 1;'}
`;

const LeftBlock = styled.div``;

const Left = styled.div`
	width: 215px;
	position: relative;

	@media (max-width: 900px) {
		position: absolute;
		width: 140px;
	}

	${LeftBlock} {
		position: sticky;
		top: 90px;
		left: 0;
		margin-left: 60px;
		margin-top: 10px;

		@media (max-width: 900px) {
			margin-left: 0;

			${Share} {
				display: none;
			}
		}
	}
`;

const Center = styled.div`
	width: 720px;
	max-width: 100%;
	margin-right: 82px;

	@media (max-width: 990px) {
		margin-right: 52px;
	}
`;

const Right = styled.div`
	width: 25%;
	min-width: 215px;
	padding-top: 75px;
	max-width: 215px;

	@media (max-width: 690px) {
		width: 100%;
		min-width: 100%;
		padding-top: 0;
	}
`;

const PostContainer = styled.div`
	width: 100%;

	@media (min-width: 901px) {
		${Share} {
			display: none;
		}
	}
`;

const PostTitle = styled.h1`
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 42px;
	line-height: 50px;
	color: var(--color-purple);
	padding-top: 68px;

	@media (max-width: 1070px) {
		font-size: 34px;
		line-height: 43px;
	}

	@media (max-width: 800px) {
		font-size: 30px;
		line-height: 38px;
		padding-top: 90px;
	}

	@media (max-width: 700px) {
		font-size: 28px;
		padding-top: 90px;
	}
`;

const MetaRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 32px;
`;

const AuthorImage = styled.img`
	display: block;
	margin-right: 9px;
	border-radius: 17px;
	overflow: hidden;
	width: 50px;
`;

const Name = styled.div`
	font-family: var(--font-sans);
	font-weight: var(--font-medium);
	font-size: 14px;
	line-height: 20px;
	color: var(--color-black);
	margin-top: 2px;
`;

const Position = styled.div`
	font-family: var(--font-sans);
	font-weight: var(--font-medium);
	font-size: 14px;
	line-height: 20px;
	color: var(--color-purple-50);
`;

const PostDate = styled.div`
	font-family: var(--font-sans);
	font-weight: var(--font-medium);
	font-size: 14px;
	line-height: 20px;
	text-align: right;
	color: var(--color-purple-50);
`;

const Preview = styled.img`
	display: block;
	margin-top: 40px;
	width: 100%;
	border-radius: 63px;
	margin-bottom: 40px;
	overflow: hidden;

	@media (max-width: 850px) {
		border-radius: 43px;
	}
`;

const Author = styled.div`
	display: flex;
	align-items: flex-start;
`;

const Content = styled.div`
	margin-bottom: 74px;

	@media (max-width: 900px) {
		margin-bottom: 0;
	}

	blockquote {
		position: relative;
		padding-left: 70px;
		padding-right: 20px;

		p {
			font-family: var(--font-sans);
			font-size: 28px;
			line-height: 37px;
			color: var(--color-black);
			margin-bottom: 22px;
		}

		p:before,
		p:after {
			content: '"';
		}

		&:before {
			width: 44px;
			height: 37px;
			position: absolute;
			left: 0px;
			top: 6px;
			content: '';
			background-image: url('/images/icons/quote-icon.svg');
			background-repeat: no-repeat;

			@media (max-width: 600px) {
				width: 30px;
			}

			@media (max-width: 500px) {
				width: 27px;
				left: -2px;
			}
		}
	}

	iframe {
		width: 100%;
		height: 400px;
		display: flex;
		margin: 0 auto;

		+ p {
			i {
				font-size: 14px;
				line-height: 25px;
			}

			> i {
				color: var(--color-purple-50);
			}
		}
	}

	figure {
		margin-top: 40px;
		display: flex;
		flex-direction: column;
		align-items: center;

		figcaption {
			font-family: var(--font-sans);

			font-size: 14px;
			line-height: 25px;
			color: var(--color-purple-50);
			padding-left: 20px;
			text-align: center;
		}
	}

	i {
		font-style: italic;
	}

	img {
		max-width: 100%;
		display: block;
		margin: 10px 0;
		width: 100%;
		height: auto;
	}

	a {
		color: var(--color-purple);
		text-decoration: none;
	}

	p {
		font-family: var(--font-sans);
		font-size: 18px;
		line-height: 30px;
		color: var(--color-black);
		margin-top: 10px;

		@media (max-width: 500px) {
			margin-top: 5px;
		}

		i {
			font-style: italic;
		}
	}

	h3 {
		font-family: var(--font-sans);
		font-weight: var(--font-bold);
		font-size: 18px;
		line-height: 30px;
		color: var(--color-black);
		margin-top: 30px;

		@media (max-width: 500px) {
			margin-top: 20px;
		}
	}

	h4 {
		font-family: var(--font-sans);
		font-weight: var(--font-bold);
		font-size: 18px;
		line-height: 30px;
		color: var(--color-black);
		margin-top: 30px;

		@media (max-width: 500px) {
			margin-top: 20px;
		}
	}

	h1,
	h2,
	h5,
	h6 {
		font-family: var(--font-sans);
		font-size: 28px;
		line-height: 35px;
		color: var(--color-black);
		margin-top: 10px;
		max-width: 685px;

		@media (max-width: 1070px) {
			font-size: 23px;
			line-height: 37px;
		}
	}

	iframe {
		margin-top: 50px;
	}

	strong {
		font-weight: var(--font-bold);
	}

	ol {
		margin: 40px 0 20px 0;
		padding-left: 30px;

		li {
			font-family: var(--font-sans);
			font-size: 18px;
			line-height: 25px;
			color: var(--color-black);
			margin: 15px 0 30px;
			padding-left: 20px;
			position: relative;
			list-style-type: decimal;

			@media (max-width: 500px) {
				padding-left: 15px;
				margin-bottom: 20px;
			}

			&::marker {
				color: var(--color-black);
			}

			br {
				margin-top: 10px;
				display: block;
				content: '';
			}

			h1,
			h2,
			h3,
			h4,
			h5,
			h6 {
				font-family: var(--font-sans);
				font-size: 18px;
				line-height: 25px;
				margin: 0;
				color: var(--color-black);
			}

			p {
				font-family: var(--font-sans);
				font-size: 18px;
				line-height: 25px;
				color: var(--color-purple-50);
				margin: 0;
			}
		}
	}

	ul {
		margin: 20px 0 10px 0;
		padding-left: 30px;

		li {
			font-family: var(--font-sans);
			font-size: 18px;
			line-height: 25px;
			color: var(--color-black);
			margin: 15px 0 30px;
			padding-left: 20px;
			position: relative;
			list-style-image: url('/images/blog/heart.svg');
			list-style-type: ' ';

			@media (max-width: 500px) {
				padding-left: 15px;
				margin-bottom: 20px;
			}

			h1,
			h2,
			h3,
			h4,
			h5,
			h6 {
				font-family: var(--font-sans);
				font-size: 18px;
				line-height: 25px;
				color: var(--color-black);
				margin: 0;
			}

			p {
				font-family: var(--font-sans);
				font-size: 18px;
				line-height: 25px;
				color: var(--color-purple-50);
				margin: 0;
			}
		}
	}
`;

export async function getStaticProps({ params }) {
	const { lang, url } = params;

	if (!url) return { notFound: true };

	const [page, allPosts, currentPost] = await Promise.all([
		fetchAPI('/blog-home', { lang }),
		fetchAPI('/blog-posts', {
			lang: 'en',
			sort: 'date:desc',
			limit: BLOG_POSTS_API_LIMIT,
			populate: ['article', 'preview', 'preview.file'],
		}),
		fetchAPI('/blog-posts', {
			url,
			lang: 'en',
			populate: [
				'author',
				'author.author',
				'author.authorImage',
				'author.authorImage.file',
				'article',
				'preview',
				'preview.file',
				'tags',
				'categories',
			],
		}),
	]);

	const post = currentPost?.data?.[0];

	if (!post) return { notFound: true };

	const categories = post?.categories;
	const recommended: any = [];

	page?.data?.promotedPosts
		?.map(({ url }) => url)
		?.forEach((url) => {
			allPosts?.data?.forEach((post) => {
				if (url === post.url) recommended.push(post);
			});
		});

	let related = allPosts?.data?.slice(0, 3);

	if (categories?.[0]) {
		const category = await fetchAPI('/blog-posts-categories', {
			lang,
			url: categories?.[0]?.url,
			populate: [
				'blogPosts',
				'blogPosts.article',
				'blogPosts.preview',
				'blogPosts.preview.file',
			],
		});

		const relatedByCategory = category?.data?.[0]?.blogPosts?.filter(
			(post) => post.url !== url,
		);
		const filteredPosts = allPosts?.data?.filter((post) => post.url !== url);

		relatedByCategory?.forEach((post, index) => {
			if (post.url !== filteredPosts?.[index]?.url) filteredPosts.push(post);
		});

		related = filteredPosts.slice(0, 3);
	}

	return {
		props: { url, lang, post, recommended, related },
	};
}

export async function getStaticPaths() {
	const entries = await fetchAPI('/blog-posts', {
		sort: 'date:desc',
		limit: BLOG_POSTS_API_LIMIT,
	});
	const paths: any = [];

	entries?.data?.forEach((entry) => {
		LANGUAGES.forEach((lang) => {
			paths.push({ params: { url: entry.url, lang: lang.code } });
		});
	});

	return {
		paths,
		fallback: false,
	};
}

export default PostPage;
